import React from "react";

// Only the tags markdownToHtml can emit. Anything else stays plain text, so a
// user-supplied label can never smuggle markup in.
const ALLOWED_TAGS = {
  strong: "strong",
  b: "strong",
  em: "em",
  i: "em",
  s: "s",
  del: "s",
  u: "u",
  code: "code",
  br: "br",
  a: "a",
};

const VOID_TAGS = { br: true };

const CLASS_NAMES = {
  a: "underline hover:text-primary",
  code: "font-mono rounded bg-gray-100 px-1 py-0.5",
};

const TAG = /<(\/)?([a-zA-Z][a-zA-Z0-9]*)([^>]*?)\/?>/g;
const HREF = /href\s*=\s*"([^"]*)"/i;
const SAFE_URL = /^(?:https?:\/\/|mailto:|tel:|\/|#)/i;

const ENTITIES = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};

function decodeEntities(text) {
  return text.replace(
    /&(?:amp|lt|gt|quot|#39|nbsp);/g,
    (entity) => ENTITIES[entity],
  );
}

function propsFor(tag, attributes) {
  if (tag !== "a") return { className: CLASS_NAMES[tag] };

  const href = attributes.match(HREF)?.[1];
  return {
    className: CLASS_NAMES.a,
    href: href && SAFE_URL.test(decodeEntities(href)) ? href : undefined,
    target: "_blank",
    rel: "noopener noreferrer",
  };
}

function parse(html) {
  const root = { children: [] };
  const stack = [root];
  const current = () => stack[stack.length - 1];
  const pushText = (text) => {
    if (text) current().children.push(decodeEntities(text));
  };

  let cursor = 0;
  let match;
  TAG.lastIndex = 0;

  while ((match = TAG.exec(html)) !== null) {
    const [raw, closing, name, attributes] = match;
    const tag = ALLOWED_TAGS[name.toLowerCase()];
    if (!tag) continue;
    if (closing && !stack.some((node) => node.tag === tag)) continue;

    pushText(html.slice(cursor, match.index));
    cursor = match.index + raw.length;

    if (VOID_TAGS[tag]) {
      current().children.push({ tag, props: {}, children: [] });
    } else if (closing) {
      while (current().tag !== tag) stack.pop();
      stack.pop();
    } else {
      const node = { tag, props: propsFor(tag, attributes), children: [] };
      current().children.push(node);
      stack.push(node);
    }
  }

  pushText(html.slice(cursor));
  return root.children;
}

function toReact(nodes) {
  return nodes.map((node, index) =>
    typeof node === "string" ? (
      node
    ) : (
      <node.tag key={index} {...node.props}>
        {node.children.length ? toReact(node.children) : undefined}
      </node.tag>
    ),
  );
}

/**
 * Renders a small, fixed subset of inline HTML as React elements.
 *
 * Unlike `dangerouslySetInnerHTML` this is safe on untrusted input: tags
 * outside the allowlist and unsafe `href` values are dropped rather than
 * rendered.
 *
 * @param {string} html - HTML string, typically the output of `markdownToHtml`.
 * @returns {React.ReactNode} Rendered nodes.
 */
export default function renderInlineHtml(html) {
  if (typeof html !== "string") return html;
  return <>{toReact(parse(html))}</>;
}
